/* eslint-disable lingui/no-unlocalized-strings */
// Refund policy — port of design/source/public/refund.html.
import { t } from "@lingui/macro";
import { Link } from "react-router";

import type { LegalSection } from "@/client/components/public/legal-layout";
import { LegalLayout } from "@/client/components/public/legal-layout";
import { SEO } from "@/client/components/seo";

const SECTIONS: LegalSection[] = [
  {
    id: "overview",
    title: "1. The short version",
    body: (
      <p>
        If Apply Edge isn&apos;t right for you, we&apos;ll refund your first paid charge in full
        within <strong>14 days</strong>. No forms. No retention call. No questions about why. Just
        email{" "}
        <a className="text-primary hover:underline" href="mailto:refunds@apply-edge.com">
          refunds@apply-edge.com
        </a>{" "}
        from the address on your account.
      </p>
    ),
  },
  {
    id: "eligible",
    title: "2. What's eligible",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Your <strong>first</strong> paid charge on Apply Edge (Pro or Ultimate, monthly or yearly)
          within 14 days of the charge date.
        </li>
        <li>
          Annual plans: full refund within 14 days of the renewal date if you forgot to cancel.
        </li>
        <li>Accidental double-charges or duplicate subscriptions — refunded any time, in full.</li>
        <li>
          Failed AI generations or platform outages that materially prevented you from using a
          feature you paid for.
        </li>
      </ul>
    ),
  },
  {
    id: "ineligible",
    title: "3. What isn't eligible",
    body: (
      <ul className="list-disc space-y-2 pl-5">
        <li>
          Renewals after the first one (annual or monthly). Cancel before the renewal date and you
          won&apos;t be charged.
        </li>
        <li>Charges older than 14 days, beyond the cases listed above.</li>
        <li>
          Add-on or one-off charges (e.g. priority profile review credits) once the service has been
          delivered.
        </li>
        <li>Accounts terminated for ToS violations (chargeback fraud, spam, abuse).</li>
      </ul>
    ),
  },
  {
    id: "process",
    title: "4. How to request a refund",
    body: (
      <>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            Email{" "}
            <a className="text-primary hover:underline" href="mailto:refunds@apply-edge.com">
              refunds@apply-edge.com
            </a>{" "}
            from the address on your account.
          </li>
          <li>
            Include the last 4 digits of the card and the approximate charge date if you can — it
            speeds things up.
          </li>
          <li>
            You&apos;ll get a confirmation within <strong>1 business day</strong>.
          </li>
          <li>
            The refund posts back to the original card within <strong>5 business days</strong>.
            (Your bank may take another 2–3 days to display it.)
          </li>
        </ol>
        <p className="mt-4">
          If you don&apos;t hear back in 24h on a weekday, please ping{" "}
          <a className="text-primary hover:underline" href="mailto:hello@apply-edge.com">
            hello@apply-edge.com
          </a>{" "}
          — sometimes mail filters are weird.
        </p>
      </>
    ),
  },
  {
    id: "data",
    title: "5. What happens to your account",
    body: (
      <>
        <p>
          After a refund, your subscription drops to Free at the next billing event. Your resumes,
          applications, and history stay intact and accessible on the Free tier. If you exceed
          Free&apos;s caps, the oldest items are archived (read-only) — not deleted — and reactivate
          when you upgrade again.
        </p>
        <p className="mt-4">
          Want a full account deletion instead of a tier downgrade? See{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            our privacy policy
          </Link>{" "}
          or write to{" "}
          <a className="text-primary hover:underline" href="mailto:privacy@apply-edge.com">
            privacy@apply-edge.com
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "chargebacks",
    title: "6. A note on chargebacks",
    body: (
      <p>
        If you initiate a bank chargeback before contacting us, we lose the ability to refund
        directly and your account is frozen until the dispute resolves. Please write to us first —
        refunds are faster, less painful, and won&apos;t affect your credit profile.
      </p>
    ),
  },
  {
    id: "contact",
    title: "7. Questions",
    body: (
      <>
        <p>
          Refunds:{" "}
          <a className="text-primary hover:underline" href="mailto:refunds@apply-edge.com">
            refunds@apply-edge.com
          </a>
        </p>
        <p>
          General:{" "}
          <a className="text-primary hover:underline" href="mailto:hello@apply-edge.com">
            hello@apply-edge.com
          </a>
        </p>
        <p className="mt-3 text-[12.5px] text-muted-foreground">
          Apply Edge (entity formation in progress), Dubai, UAE (registered address pending)
        </p>
      </>
    ),
  },
];

export const RefundPage = () => (
  <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
    {/*
      canonical is "/refund-policy", not "/refund". This page is routed at
      both paths, but sitemap.xml + robots.txt both declare /refund-policy —
      the old hardcoded /refund canonical told Google to drop the very URL
      the sitemap asked it to index. Keep the two in sync.
    */}
    <SEO
      title={t`Refund policy — Apply Edge`}
      description={t`Apply Edge refund policy. 14-day full refund on your first paid charge. Email us, no forms.`}
      canonical="/refund-policy"
    />
    <LegalLayout
      titleHead="Refund policy"
      meta="Last updated April 27, 2026"
      summary={{
        label: "In one line",
        body: "14-day full refund on your first paid charge. Email us, no forms.",
      }}
      sections={SECTIONS}
      trailing={
        <div className="mt-12 rounded-2xl border border-border bg-surface p-6">
          <div className="text-xs font-medium text-muted-foreground">Need a refund right now?</div>
          <p className="mt-2 text-[14px] text-foreground">
            Write one sentence to{" "}
            <a className="text-primary hover:underline" href="mailto:refunds@apply-edge.com">
              refunds@apply-edge.com
            </a>{" "}
            and we&apos;ll handle the rest.
          </p>
        </div>
      }
    />
  </div>
);
